import React from "react";
import { SubscriptionActionFooter } from "./SubscriptionActionDialog";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import { SwapHorizRounded, ArrowForwardRounded } from "@mui/icons-material";

export default function SubscriptionPlanAction({
  sub,
  plans,
  targetPlan,
  setTargetPlan,
  planChangePreview,
  submitPlanMigration,
  updating,
  refreshing,
  pricingError,
}) {
  return (
    <Stack spacing={2.5}>
      {/* Plan Selection */}
      <Stack spacing={1}>
        <Typography variant='body2' fontWeight={600}>
          New plan
        </Typography>

        <FormControl size='small' fullWidth>
          <Select
            inputProps={{ "aria-label": "New plan" }}
            value={targetPlan}
            displayEmpty
            onChange={(e) => setTargetPlan(e.target.value)}
            sx={{
              borderRadius: 1.5,
              "& .MuiSelect-select": {
                py: 1.2,
              },
            }}
          >
            <MenuItem value='' disabled>
              <Typography color='text.secondary' variant='body2'>
                Select a subscription plan
              </Typography>
            </MenuItem>

            {plans.map((p) => (
              <MenuItem key={p.id} value={p.id} disabled={p.id === sub?.planId}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  sx={{ width: "100%" }}
                >
                  <Typography variant='body2'>{p.name}</Typography>

                  {p.id === sub?.planId && (
                    <Chip
                      label='Current'
                      size='small'
                      variant='outlined'
                      sx={{ height: 20, fontSize: 10 }}
                    />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Plan Change Preview */}
      {planChangePreview && (
        <Box
          role='region'
          aria-label='Plan change preview'
          sx={{
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 2,
          }}
        >
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary'>
                  Current
                </Typography>
                <Typography variant='body2' sx={{ overflowWrap: "anywhere" }}>
                  {planChangePreview.currentPlan}
                </Typography>
              </Box>
              <ArrowForwardRounded
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary'>
                  New
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color='primary.main'
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {planChangePreview.nextPlan}
                </Typography>
              </Box>
            </Box>
            <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap'>
              <Chip
                size='small'
                variant='outlined'
                label={planChangePreview.discountLabel}
              />
              {planChangePreview.discountSource && (
                <Typography variant='caption' color='text.secondary'>
                  {planChangePreview.discountSource}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Action */}
      <SubscriptionActionFooter>
        <Button
          variant='contained'
          disableElevation
          onClick={submitPlanMigration}
          disabled={
            sub?.status === "expired" ||
            !targetPlan ||
            targetPlan === sub.planId ||
            updating ||
            refreshing ||
            Boolean(pricingError)
          }
          startIcon={
            updating ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <SwapHorizRounded fontSize='small' />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 2.5,
            minHeight: 38,
          }}
        >
          {updating ? "Changing..." : "Change Plan"}
        </Button>
      </SubscriptionActionFooter>
    </Stack>
  );
}
